function syncThumb(scroller, thumb, track) {
  const thumbRatio = scroller.clientWidth / scroller.scrollWidth;
  const maxScroll = scroller.scrollWidth - scroller.clientWidth;
  const scrollRatio = maxScroll > 0 ? scroller.scrollLeft / maxScroll : 0;
  const thumbPercent = thumbRatio * 100;

  thumb.style.width = `${thumbPercent}%`;
  thumb.style.marginInlineStart = `${scrollRatio * (100 - thumbPercent)}%`;

  track?.setAttribute('aria-valuemax', String(Math.round(maxScroll)));
  track?.setAttribute('aria-valuenow', String(Math.round(scroller.scrollLeft)));
}

function bindScrollbar(scroller, track, thumb) {
  if (track.dataset.productRecommendationsScrollbarBound) return;
  track.dataset.productRecommendationsScrollbarBound = 'true';

  const scrollFromThumbPosition = (thumbPosition) => {
    const thumbTravel = track.clientWidth - thumb.offsetWidth;
    const scrollRange = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollLeft = thumbTravel > 0 ? (thumbPosition / thumbTravel) * scrollRange : 0;
  };

  track.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch' || event.button !== 0) return;

    event.preventDefault();

    if (event.target !== thumb) {
      const trackRect = track.getBoundingClientRect();
      const targetPosition = Math.min(
        Math.max(event.clientX - trackRect.left - thumb.offsetWidth / 2, 0),
        track.clientWidth - thumb.offsetWidth,
      );
      scrollFromThumbPosition(targetPosition);
    }

    const startX = event.clientX;
    const startScrollLeft = scroller.scrollLeft;
    const thumbTravel = track.clientWidth - thumb.offsetWidth;
    const scrollRange = scroller.scrollWidth - scroller.clientWidth;

    track.setPointerCapture(event.pointerId);
    track.classList.add('product-recommendations__scrollbar--dragging');
    scroller.classList.add('product-recommendations__scroller--dragging');

    const handlePointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== event.pointerId || thumbTravel <= 0) return;
      const scrollDelta = (moveEvent.clientX - startX) * (scrollRange / thumbTravel);
      scroller.scrollLeft = Math.min(Math.max(startScrollLeft + scrollDelta, 0), scrollRange);
    };

    const stopDragging = (endEvent) => {
      if (endEvent.pointerId !== event.pointerId) return;
      track.classList.remove('product-recommendations__scrollbar--dragging');
      scroller.classList.remove('product-recommendations__scroller--dragging');
      track.removeEventListener('pointermove', handlePointerMove);
      track.removeEventListener('pointerup', stopDragging);
      track.removeEventListener('pointercancel', stopDragging);
    };

    track.addEventListener('pointermove', handlePointerMove);
    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
  });

  track.addEventListener('keydown', (event) => {
    const step = Math.max(scroller.clientWidth * 0.15, 40);

    if (event.key === 'ArrowLeft') scroller.scrollBy({ left: -step, behavior: 'smooth' });
    else if (event.key === 'ArrowRight') scroller.scrollBy({ left: step, behavior: 'smooth' });
    else if (event.key === 'Home') scroller.scrollTo({ left: 0, behavior: 'smooth' });
    else if (event.key === 'End') scroller.scrollTo({ left: scroller.scrollWidth, behavior: 'smooth' });
    else return;

    event.preventDefault();
  });
}

function updateCarousel(carousel) {
  const scroller = carousel.querySelector('slideshow-slides');
  const track = carousel.querySelector('[data-product-recommendations-scrollbar]');
  const thumb = track?.querySelector('[data-product-recommendations-scrollbar-thumb]');

  if (!scroller || !track || !thumb) return;

  const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;
  track.classList.toggle('product-recommendations__scrollbar--visible', hasOverflow);

  if (!hasOverflow) return;

  syncThumb(scroller, thumb, track);
  bindScrollbar(scroller, track, thumb);

  if (!scroller.dataset.productRecommendationsScrollbarBound) {
    scroller.dataset.productRecommendationsScrollbarBound = 'true';
    scroller.addEventListener('scroll', () => syncThumb(scroller, thumb, track), { passive: true });
  }
}

function updateSection(section) {
  section.querySelectorAll('[data-product-recommendations-carousel]').forEach(updateCarousel);
}

function init(section) {
  if (section.dataset.productRecommendationsScrollbarInitialized) {
    updateSection(section);
    return;
  }

  section.dataset.productRecommendationsScrollbarInitialized = 'true';
  const observer = new MutationObserver(() => updateSection(section));
  observer.observe(section, { childList: true, subtree: true });
  updateSection(section);
}

function initAll(root = document) {
  root.querySelectorAll('product-recommendations').forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

window.addEventListener('resize', () => initAll());
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
